// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// The first route is `/website` and it is the DASHBOARD, not the inbox — unlike
// every other module, where the first screen is a list of work. Traffic is what
// somebody opens this module to look at; the leads are a tab away and announce
// themselves anyway, which is the whole point of the notification.
//
// What is NOT here: anything to do with the tracker script. That is not part of
// this SPA — it is a file served to somebody else's website (frontend/embed/),
// and importing it here would be the first step towards it depending on
// something the ui-kit provides.

export const routes = [
  {
    path: '/website',
    name: 'website',
    component: () => import('./pages/WebsitePage.vue'),
    meta: { permission: 'website:read', title: 'website.title' },
  },
  {
    path: '/website/leads',
    name: 'website-leads',
    component: () => import('./pages/LeadsPage.vue'),
    meta: { permission: 'website:read', title: 'website.leads.title' },
  },
  {
    path: '/website/leads/:id',
    name: 'website-lead',
    component: () => import('./pages/LeadPage.vue'),
    meta: { permission: 'website:read', title: 'website.lead.title' },
  },
]

export const navigation = [
  {
    to: '/website',
    labelKey: 'website.title',
    permission: 'website:read',
    icon: 'globe',
    group: 'business',
    children: [
      { to: '/website', labelKey: 'website.nav.traffic', permission: 'website:read' },
      { to: '/website/leads', labelKey: 'website.leads.title', permission: 'website:read' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client.
const baseMessages = {
  es: {
    website: {
      title: 'Web',
      dashboard: {
        visits: 'Visitas (30 días)',
        visitsHelp: 'Páginas vistas en los últimos treinta días, con el cambio respecto a los treinta anteriores.',
        sessions: 'Sesiones (30 días)',
        sessionsHelp: 'Visitas distintas en los últimos treinta días. Una sesión no es una persona: no hay cookie ni identificador.',
        leads: 'Contactos (30 días)',
        leadsHelp: 'Cuánta gente os ha escrito desde la web en treinta días, sin contar el spam.',
        conversion: 'Conversión',
        conversionHelp: 'Qué porcentaje de las sesiones acabó en alguien escribiendo. Vacío si no hubo sesiones.',
        latestLeads: 'Contactos nuevos',
        latestLeadsHelp: 'Los contactos sin abrir, del más nuevo al más antiguo. Estira la tarjeta y caben más.',
      },
      subtitle: 'Qué pasa en vuestra web y quién os escribe desde ella',
      nav: { traffic: 'Visitas' },

      // El título y el resumen del tema de ayuda. Van aquí y no dentro del
      // `help/es.md` porque el índice de la documentación los necesita antes de
      // abrir nada, y cargar veinticinco módulos por cuatro idiomas para
      // dibujar una lista serían cientos de kilobytes en el arranque.
      help: {
        title: 'Web',
        subtitle: 'Las visitas de vuestra web y los contactos que llegan del formulario, con el código que hay que pegar en la web',
      },

      filters: {
        site: 'Sitio', allSites: 'Todos los sitios', period: 'Periodo',
        searchPlaceholder: 'Nombre, correo, empresa…',
      },
      // Corto en el botón, entero al posarse encima: cuatro opciones a la vista
      // sólo caben si cada una es de dos palabras, y «7 días» sin más podría
      // leerse como «los siete días que vienen».
      periods: {
        week: '7 días', month: '30 días', quarter: '90 días', year: '1 año',
        weekTitle: 'Últimos 7 días', monthTitle: 'Últimos 30 días',
        quarterTitle: 'Últimos 90 días', yearTitle: 'Último año',
      },

      stats: {
        sessions: 'Visitas', views: 'Páginas vistas', leads: 'Contactos',
        conversion: 'Conversión', newLeads: 'Sin abrir',
      },
      against: 'Frente al periodo anterior ({from} — {to}):',
      noComparison: 'No hay periodo anterior con el que comparar todavía.',

      chart: { title: 'Visitas por día', nothing: 'Todavía no ha llegado ninguna visita' },
      pages: { title: 'Páginas más vistas', page: 'Página', nothing: 'Sin datos' },
      sources: {
        title: 'De dónde vienen', source: 'Origen', nothing: 'Sin datos',
        direct: 'Directo', search: 'Buscadores', social: 'Redes sociales',
        internal: 'Desde la propia web',
      },

      silent: 'Hace {days} días que {site} no manda ninguna visita. Lo normal es que el código se haya perdido en un cambio de la web.',
      privacyNote: 'Aquí se cuentan visitas, no personas: no se guarda ningún dato del visitante ni se puede seguir a nadie de una página a otra.',

      leads: {
        title: 'Contactos', subtitle: 'Quien os ha escrito desde la web',
        nothing: 'Todavía no os ha escrito nadie',
        all: 'Todos', who: 'Quién', about: 'Sobre qué', from: 'De dónde',
        state: 'Estado', when: 'Cuándo',
        previous: 'Anteriores', next: 'Siguientes',
      },

      status: {
        new: 'Sin abrir', contacted: 'Contestado', qualified: 'Aceptado',
        discarded: 'Descartado', spam: 'Filtrado',
      },

      lead: {
        title: 'Contacto', message: 'Lo que escribió', extraFields: 'Otros campos del formulario',
        history: 'Historial', notePlaceholder: 'Añadir una nota…',
        contact: 'Cómo contactar', email: 'Correo', phone: 'Teléfono', company: 'Empresa',
        origin: 'De dónde viene', site: 'Sitio', page: 'Página', when: 'Recibido',
        checked: 'Comprobación', filtered: 'Filtrado por', assignee: 'Lo lleva',
        nobody: 'Nadie todavía', assigneePlaceholder: 'Buscar a alguien…',
      },

      steps: {
        received: 'Llegó desde la web', filtered: 'Filtrado automáticamente',
        contacted: 'Marcado como contestado', qualified: 'Aceptado como cliente potencial',
        discarded: 'Descartado', spam: 'Movido a filtrados', new: 'Devuelto a sin abrir',
        assigned: 'Asignado', note: 'Nota',
      },

      actions: {
        contacted: 'Marcar como contestado', qualify: 'Aceptar', discard: 'Descartar',
        delete: 'Eliminar', note: 'Añadir', cancelAction: 'Cancelar', take: 'Me lo quedo',
      },

      qualify: {
        title: 'Aceptar el contacto',
        withCrm: 'Se abrirá una ficha de cliente con estos datos. Es lo único que llega: lo que no pongáis aquí, allí no está.',
        withoutCrm: 'El contacto queda aceptado. Sin el módulo de clientes instalado no se abre ninguna ficha.',
        name: 'Nombre del cliente', taxId: 'NIF / CIF',
        taxIdHint: 'Si lo sabéis. Se puede completar después en la ficha.',
        done: 'Contacto aceptado',
      },

      delete: {
        title: 'Eliminar el contacto',
        question: 'Se borra del todo, no se archiva. Es la forma de atender a alguien que pide que le olvidéis.',
      },

      captcha: {
        verified: 'Verificado', unverified: 'No superó la verificación',
        none: 'Sin verificación configurada',
      },
      spamReasons: {
        honeypot: 'Rellenó un campo oculto', too_fast: 'Se envió demasiado rápido',
        links: 'Demasiados enlaces', captcha: 'No superó el captcha',
      },

      search: { subtitle: 'Contacto desde la web' },

      notifications: {
        newLead: 'Alguien escribe desde la web',
        lead: {
          title: 'Nuevo contacto desde la web',
          body: '{name} ha escrito desde {site}{page}.',
        },
      },
    },

    modules: { website: 'Web' },
    assistant: {
      openers: {
        website: {
          'traffic-week': '¿Cómo ha ido la web esta semana?',
          'new-leads': '¿Quién nos ha escrito desde la web?',
          'top-pages': '¿Qué páginas son las más vistas?',
        },
      },
    },
    permissions: {
      website: {
        read: 'Ver las visitas y los contactos de la web',
        write: 'Contestar, aceptar y descartar contactos',
        manage: 'Eliminar contactos y ver lo filtrado',
      },
    },
    settings: {
      ns: {
        website: {
          title: 'Web',
          subtitle: 'A quién se avisa de un contacto y cuánto tiempo se guarda lo que llega',
          notifyLeads: { label: 'Avisar cuando llega un contacto' },
          notifyRoles: {
            label: 'Roles a los que se avisa',
            help: 'Roles, no personas, para que siga funcionando cuando alguien se va.',
          },
          minimumSeconds: {
            label: 'Segundos mínimos antes de creerse un formulario',
            help: 'Por debajo, se guarda como filtrado. Una persona escribiendo tarda más que esto.',
          },
          maxLinks: {
            label: 'Enlaces en un mensaje antes de darlo por filtrado',
            help: 'A partir de este número, el envío se guarda como filtrado. Una consulta de verdad rara vez lleva más de uno.',
          },
          silenceDays: {
            label: 'Días sin visitas para dar la web por muda',
            help: 'Una web que deja de mandar visitas suele ser el código perdido en un cambio de la web.',
          },
          leadRetentionDays: {
            label: 'Días que se guarda un contacto',
            help: 'Cero los guarda hasta que alguien los borre. Los aceptados se guardan siempre: la ficha de cliente está en otro sitio.',
          },
          spamRetentionDays: {
            label: 'Días que se guarda lo filtrado',
            help: 'Lo bastante para comprobar que el filtro no se come mensajes de verdad, no tanto como para ser un vertedero.',
          },
          trafficRetentionDays: {
            label: 'Días que se guardan los contadores de visitas',
            help: 'Dos años, para poder comparar este con el anterior. Los contadores no nombran a nadie.',
          },
        },
      },
    },
    audit: {
      actions: {
        website: {
          lead_captured: 'Contacto recibido desde la web',
          lead_filtered: 'Contacto filtrado automáticamente',
          lead_contacted: 'Contacto marcado como contestado',
          lead_qualified: 'Contacto aceptado como cliente potencial',
          lead_discarded: 'Contacto descartado',
          lead_spam: 'Contacto movido a filtrados',
          lead_new: 'Contacto devuelto a sin abrir',
          lead_assigned: 'Contacto asignado',
          lead_deleted: 'Contacto eliminado',
        },
      },
    },
  },

  ca: {
    website: {
      title: 'Web',
      dashboard: {
        visits: 'Visites (30 dies)',
        visitsHelp: 'Pàgines vistes els últims trenta dies, amb el canvi respecte als trenta anteriors.',
        sessions: 'Sessions (30 dies)',
        sessionsHelp: 'Visites diferents els últims trenta dies. Una sessió no és una persona: no hi ha galeta ni identificador.',
        leads: 'Contactes (30 dies)',
        leadsHelp: 'Quanta gent us ha escrit des del web en trenta dies, sense comptar el correu brossa.',
        conversion: 'Conversió',
        conversionHelp: 'Quin percentatge de les sessions va acabar amb algú escrivint. Buit si no hi va haver sessions.',
        latestLeads: 'Contactes nous',
        latestLeadsHelp: 'Els contactes sense obrir, del més nou al més antic. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'Què passa al vostre web i qui us escriu des d’allà',
      nav: { traffic: 'Visites' },

      help: {
        title: 'Web',
        subtitle: 'Les visites del vostre web i els contactes que arriben del formulari, amb el codi que cal enganxar al web',
      },

      filters: {
        site: 'Lloc', allSites: 'Tots els llocs', period: 'Període',
        searchPlaceholder: 'Nom, correu, empresa…',
      },
      periods: {
        week: '7 dies', month: '30 dies', quarter: '90 dies', year: '1 any',
        weekTitle: 'Últims 7 dies', monthTitle: 'Últims 30 dies',
        quarterTitle: 'Últims 90 dies', yearTitle: 'Últim any',
      },

      stats: {
        sessions: 'Visites', views: 'Pàgines vistes', leads: 'Contactes',
        conversion: 'Conversió', newLeads: 'Sense obrir',
      },
      against: 'Respecte al període anterior ({from} — {to}):',
      noComparison: 'Encara no hi ha cap període anterior amb què comparar.',

      chart: { title: 'Visites per dia', nothing: 'Encara no ha arribat cap visita' },
      pages: { title: 'Pàgines més vistes', page: 'Pàgina', nothing: 'Sense dades' },
      sources: {
        title: 'D’on vénen', source: 'Origen', nothing: 'Sense dades',
        direct: 'Directe', search: 'Cercadors', social: 'Xarxes socials',
        internal: 'Des del mateix web',
      },

      silent: 'Fa {days} dies que {site} no envia cap visita. El més normal és que el codi s’hagi perdut en un canvi del web.',
      privacyNote: 'Aquí es compten visites, no persones: no es guarda cap dada del visitant ni es pot seguir ningú d’una pàgina a una altra.',

      leads: {
        title: 'Contactes', subtitle: 'Qui us ha escrit des del web',
        nothing: 'Encara no us ha escrit ningú',
        all: 'Tots', who: 'Qui', about: 'Sobre què', from: 'D’on',
        state: 'Estat', when: 'Quan',
        previous: 'Anteriors', next: 'Següents',
      },

      status: {
        new: 'Sense obrir', contacted: 'Contestat', qualified: 'Acceptat',
        discarded: 'Descartat', spam: 'Filtrat',
      },

      lead: {
        title: 'Contacte', message: 'Què va escriure', extraFields: 'Altres camps del formulari',
        history: 'Historial', notePlaceholder: 'Afegir una nota…',
        contact: 'Com contactar', email: 'Correu', phone: 'Telèfon', company: 'Empresa',
        origin: 'D’on ve', site: 'Lloc', page: 'Pàgina', when: 'Rebut',
        checked: 'Comprovació', filtered: 'Filtrat per', assignee: 'Ho porta',
        nobody: 'Encara ningú', assigneePlaceholder: 'Cercar algú…',
      },

      steps: {
        received: 'Va arribar des del web', filtered: 'Filtrat automàticament',
        contacted: 'Marcat com a contestat', qualified: 'Acceptat com a client potencial',
        discarded: 'Descartat', spam: 'Mogut a filtrats', new: 'Tornat a sense obrir',
        assigned: 'Assignat', note: 'Nota',
      },

      actions: {
        contacted: 'Marcar com a contestat', qualify: 'Acceptar', discard: 'Descartar',
        delete: 'Eliminar', note: 'Afegir', cancelAction: 'Cancel·lar', take: 'Me’l quedo',
      },

      qualify: {
        title: 'Acceptar el contacte',
        withCrm: 'S’obrirà una fitxa de client amb aquestes dades. És l’únic que hi arriba: el que no poseu aquí, allà no hi és.',
        withoutCrm: 'El contacte queda acceptat. Sense el mòdul de clients instal·lat no s’obre cap fitxa.',
        name: 'Nom del client', taxId: 'NIF / CIF',
        taxIdHint: 'Si el sabeu. Es pot completar després a la fitxa.',
        done: 'Contacte acceptat',
      },

      delete: {
        title: 'Eliminar el contacte',
        question: 'S’esborra del tot, no s’arxiva. És la manera d’atendre algú que demana que l’oblideu.',
      },

      captcha: {
        verified: 'Verificat', unverified: 'No va superar la verificació',
        none: 'Sense verificació configurada',
      },
      spamReasons: {
        honeypot: 'Va omplir un camp ocult', too_fast: 'Es va enviar massa ràpid',
        links: 'Massa enllaços', captcha: 'No va superar el captcha',
      },

      search: { subtitle: 'Contacte des del web' },

      notifications: {
        newLead: 'Algú escriu des del web',
        lead: {
          title: 'Nou contacte des del web',
          body: '{name} ha escrit des de {site}{page}.',
        },
      },
    },

    modules: { website: 'Web' },
    assistant: {
      openers: {
        website: {
          'traffic-week': 'Com ha anat el web aquesta setmana?',
          'new-leads': 'Qui ens ha escrit des del web?',
          'top-pages': 'Quines pàgines són les més vistes?',
        },
      },
    },
    permissions: {
      website: {
        read: 'Veure les visites i els contactes del web',
        write: 'Contestar, acceptar i descartar contactes',
        manage: 'Eliminar contactes i veure el que s’ha filtrat',
      },
    },
    settings: {
      ns: {
        website: {
          title: 'Web',
          subtitle: 'A qui s’avisa d’un contacte i quant temps es guarda el que arriba',
          notifyLeads: { label: 'Avisar quan arriba un contacte' },
          notifyRoles: {
            label: 'Rols als quals s’avisa',
            help: 'Rols, no persones, perquè continuï funcionant quan algú marxa.',
          },
          minimumSeconds: {
            label: 'Segons mínims abans de creure’s un formulari',
            help: 'Per sota, es guarda com a filtrat. Una persona escrivint triga més que això.',
          },
          maxLinks: {
            label: 'Enllaços en un missatge abans de donar-lo per filtrat',
            help: 'A partir d’aquest nombre, l’enviament es guarda com a filtrat. Una consulta de veritat rarament en porta més d’un.',
          },
          silenceDays: {
            label: 'Dies sense visites per donar el web per mut',
            help: 'Un web que deixa d’enviar visites sol ser el codi perdut en un canvi del web.',
          },
          leadRetentionDays: {
            label: 'Dies que es guarda un contacte',
            help: 'Zero els guarda fins que algú els esborri. Els acceptats es guarden sempre: la fitxa de client és en un altre lloc.',
          },
          spamRetentionDays: {
            label: 'Dies que es guarda el que s’ha filtrat',
            help: 'Prou per comprovar que el filtre no es menja missatges de debò, no tant com per ser un abocador.',
          },
          trafficRetentionDays: {
            label: 'Dies que es guarden els comptadors de visites',
            help: 'Dos anys, per poder comparar aquest amb l’anterior. Els comptadors no anomenen ningú.',
          },
        },
      },
    },
    audit: {
      actions: {
        website: {
          lead_captured: 'Contacte rebut des del web',
          lead_filtered: 'Contacte filtrat automàticament',
          lead_contacted: 'Contacte marcat com a contestat',
          lead_qualified: 'Contacte acceptat com a client potencial',
          lead_discarded: 'Contacte descartat',
          lead_spam: 'Contacte mogut a filtrats',
          lead_new: 'Contacte tornat a sense obrir',
          lead_assigned: 'Contacte assignat',
          lead_deleted: 'Contacte eliminat',
        },
      },
    },
  },

  en: {
    website: {
      title: 'Website',
      dashboard: {
        visits: 'Views (30 days)',
        visitsHelp: 'Page views over the last thirty days, with the change against the thirty before.',
        sessions: 'Sessions (30 days)',
        sessionsHelp: 'Distinct visits over the last thirty days. A session is not a person: there is no cookie and no identifier.',
        leads: 'Enquiries (30 days)',
        leadsHelp: 'How many people wrote to you from the website in thirty days, spam excluded.',
        conversion: 'Conversion',
        conversionHelp: 'What percentage of sessions ended in somebody writing. Empty when there were no sessions.',
        latestLeads: 'New enquiries',
        latestLeadsHelp: 'The unopened enquiries, newest first. Stretch the card and more fit.',
      },
      subtitle: 'What happens on your site, and who writes from it',
      nav: { traffic: 'Traffic' },

      help: {
        title: 'Website',
        subtitle: 'Your site’s visits and the enquiries that arrive from its form, with the snippet to paste into the site',
      },

      filters: {
        site: 'Site', allSites: 'All sites', period: 'Period',
        searchPlaceholder: 'Name, email, company…',
      },
      periods: {
        week: '7 days', month: '30 days', quarter: '90 days', year: '1 year',
        weekTitle: 'Last 7 days', monthTitle: 'Last 30 days',
        quarterTitle: 'Last 90 days', yearTitle: 'Last year',
      },

      stats: {
        sessions: 'Visits', views: 'Page views', leads: 'Leads',
        conversion: 'Conversion', newLeads: 'Unopened',
      },
      against: 'Against the period before ({from} — {to}):',
      noComparison: 'There is no earlier period to compare against yet.',

      chart: { title: 'Visits per day', nothing: 'No visit has arrived yet' },
      pages: { title: 'Most visited pages', page: 'Page', nothing: 'Nothing yet' },
      sources: {
        title: 'Where they come from', source: 'Source', nothing: 'Nothing yet',
        direct: 'Direct', search: 'Search engines', social: 'Social networks',
        internal: 'From the site itself',
      },

      silent: '{site} has sent no visits for {days} days. Nine times out of ten the snippet was lost in a change to the site.',
      privacyNote: 'What is counted here is visits, not people: nothing about a visitor is stored, and nobody can be followed from one page to another.',

      leads: {
        title: 'Leads', subtitle: 'Who has written from the website',
        nothing: 'Nobody has written yet',
        all: 'All', who: 'Who', about: 'About', from: 'From',
        state: 'State', when: 'When',
        previous: 'Previous', next: 'Next',
      },

      status: {
        new: 'Unopened', contacted: 'Replied', qualified: 'Accepted',
        discarded: 'Discarded', spam: 'Filtered',
      },

      lead: {
        title: 'Lead', message: 'What they wrote', extraFields: 'Other fields on the form',
        history: 'History', notePlaceholder: 'Add a note…',
        contact: 'How to reach them', email: 'Email', phone: 'Phone', company: 'Company',
        origin: 'Where it came from', site: 'Site', page: 'Page', when: 'Received',
        checked: 'Checked', filtered: 'Filtered because', assignee: 'Handled by',
        nobody: 'Nobody yet', assigneePlaceholder: 'Search for somebody…',
      },

      steps: {
        received: 'Arrived from the website', filtered: 'Filtered automatically',
        contacted: 'Marked as replied', qualified: 'Accepted as a prospect',
        discarded: 'Discarded', spam: 'Moved to filtered', new: 'Put back as unopened',
        assigned: 'Assigned', note: 'Note',
      },

      actions: {
        contacted: 'Mark as replied', qualify: 'Accept', discard: 'Discard',
        delete: 'Delete', note: 'Add', cancelAction: 'Cancel', take: 'I will take it',
      },

      qualify: {
        title: 'Accept the lead',
        withCrm: 'A customer file will be opened with these details. It is all that travels: what you leave out here is not there.',
        withoutCrm: 'The lead is accepted. Without the customer module installed, no file is opened.',
        name: 'Customer name', taxId: 'Tax id',
        taxIdHint: 'If you know it. It can be completed later on the file.',
        done: 'Lead accepted',
      },

      delete: {
        title: 'Delete the lead',
        question: 'It is deleted outright, not archived. This is how somebody asking to be forgotten is answered.',
      },

      captcha: {
        verified: 'Verified', unverified: 'Did not pass the check',
        none: 'No check configured',
      },
      spamReasons: {
        honeypot: 'Filled in a hidden field', too_fast: 'Sent too fast',
        links: 'Too many links', captcha: 'Did not pass the captcha',
      },

      search: { subtitle: 'Lead from the website' },

      notifications: {
        newLead: 'Somebody writes from the website',
        lead: {
          title: 'New lead from the website',
          body: '{name} wrote from {site}{page}.',
        },
      },
    },

    modules: { website: 'Website' },
    assistant: {
      openers: {
        website: {
          'traffic-week': 'How did the website do this week?',
          'new-leads': 'Who has written to us from the website?',
          'top-pages': 'Which pages get the most visits?',
        },
      },
    },
    permissions: {
      website: {
        read: 'See the website traffic and the leads',
        write: 'Reply to, accept and discard leads',
        manage: 'Delete leads and see what was filtered',
      },
    },
    settings: {
      ns: {
        website: {
          title: 'Website',
          subtitle: 'Who is told about a lead, and how long what arrives is kept',
          notifyLeads: { label: 'Tell somebody when a lead arrives' },
          notifyRoles: {
            label: 'Which roles are told',
            help: 'Roles, not people, so it keeps working when somebody leaves.',
          },
          minimumSeconds: {
            label: 'Seconds a form must take before it is believed',
            help: 'Below this, a submission is filed as filtered. Somebody typing takes longer than this.',
          },
          maxLinks: {
            label: 'Links in a message before it is filed as filtered',
            help: 'At or above this count, a submission is filed as filtered. A genuine enquiry rarely carries more than one.',
          },
          silenceDays: {
            label: 'Days without a visit before the site is called silent',
            help: 'A site that stops sending visits is usually a snippet lost in a change to the site.',
          },
          leadRetentionDays: {
            label: 'Days a lead is kept',
            help: 'Zero keeps them until somebody deletes one. Accepted leads are always kept: the customer file is elsewhere.',
          },
          spamRetentionDays: {
            label: 'Days what was filtered is kept',
            help: 'Long enough to check the filter is not eating real messages, short enough not to be a landfill.',
          },
          trafficRetentionDays: {
            label: 'Days the visit counters are kept',
            help: 'Two years, so this one can be compared with the last. Counters name nobody.',
          },
        },
      },
    },
    audit: {
      actions: {
        website: {
          lead_captured: 'Lead received from the website',
          lead_filtered: 'Lead filtered automatically',
          lead_contacted: 'Lead marked as replied',
          lead_qualified: 'Lead accepted as a prospect',
          lead_discarded: 'Lead discarded',
          lead_spam: 'Lead moved to filtered',
          lead_new: 'Lead put back as unopened',
          lead_assigned: 'Lead assigned',
          lead_deleted: 'Lead deleted',
        },
      },
    },
  },

  fr: {
    website: {
      title: 'Site web',
      dashboard: {
        visits: 'Vues (30 jours)',
        visitsHelp: 'Pages vues sur les trente derniers jours, avec l\'évolution par rapport aux trente précédents.',
        sessions: 'Sessions (30 jours)',
        sessionsHelp: 'Visites distinctes sur les trente derniers jours. Une session n\'est pas une personne : ni cookie ni identifiant.',
        leads: 'Contacts (30 jours)',
        leadsHelp: 'Combien de personnes vous ont écrit depuis le site en trente jours, hors spam.',
        conversion: 'Conversion',
        conversionHelp: 'Quel pourcentage des sessions s\'est terminé par un message. Vide s\'il n\'y a eu aucune session.',
        latestLeads: 'Nouveaux contacts',
        latestLeadsHelp: 'Les contacts non ouverts, du plus nouveau au plus ancien. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Ce qui se passe sur votre site, et qui vous écrit depuis celui-ci',
      nav: { traffic: 'Visites' },

      help: {
        title: 'Site web',
        subtitle: 'Les visites de votre site et les contacts qui arrivent du formulaire, avec le code à coller dans le site',
      },

      filters: {
        site: 'Site', allSites: 'Tous les sites', period: 'Période',
        searchPlaceholder: 'Nom, courriel, entreprise…',
      },
      periods: {
        week: '7 jours', month: '30 jours', quarter: '90 jours', year: '1 an',
        weekTitle: '7 derniers jours', monthTitle: '30 derniers jours',
        quarterTitle: '90 derniers jours', yearTitle: 'Dernière année',
      },

      stats: {
        sessions: 'Visites', views: 'Pages vues', leads: 'Contacts',
        conversion: 'Conversion', newLeads: 'Non ouverts',
      },
      against: 'Par rapport à la période précédente ({from} — {to}) :',
      noComparison: 'Il n’y a pas encore de période précédente à comparer.',

      chart: { title: 'Visites par jour', nothing: 'Aucune visite n’est encore arrivée' },
      pages: { title: 'Pages les plus vues', page: 'Page', nothing: 'Aucune donnée' },
      sources: {
        title: 'D’où ils viennent', source: 'Origine', nothing: 'Aucune donnée',
        direct: 'Direct', search: 'Moteurs de recherche', social: 'Réseaux sociaux',
        internal: 'Depuis le site lui-même',
      },

      silent: '{site} n’envoie plus aucune visite depuis {days} jours. Neuf fois sur dix, le code a disparu lors d’une modification du site.',
      privacyNote: 'Ce qui est compté ici, ce sont des visites, pas des personnes : rien n’est conservé sur le visiteur et personne ne peut être suivi d’une page à l’autre.',

      leads: {
        title: 'Contacts', subtitle: 'Qui vous a écrit depuis le site',
        nothing: 'Personne ne vous a encore écrit',
        all: 'Tous', who: 'Qui', about: 'À propos de', from: 'D’où',
        state: 'État', when: 'Quand',
        previous: 'Précédents', next: 'Suivants',
      },

      status: {
        new: 'Non ouvert', contacted: 'Répondu', qualified: 'Accepté',
        discarded: 'Écarté', spam: 'Filtré',
      },

      lead: {
        title: 'Contact', message: 'Ce qu’il a écrit', extraFields: 'Autres champs du formulaire',
        history: 'Historique', notePlaceholder: 'Ajouter une note…',
        contact: 'Comment le joindre', email: 'Courriel', phone: 'Téléphone', company: 'Entreprise',
        origin: 'D’où cela vient', site: 'Site', page: 'Page', when: 'Reçu',
        checked: 'Vérification', filtered: 'Filtré pour', assignee: 'Suivi par',
        nobody: 'Personne pour l’instant', assigneePlaceholder: 'Chercher quelqu’un…',
      },

      steps: {
        received: 'Arrivé depuis le site', filtered: 'Filtré automatiquement',
        contacted: 'Marqué comme répondu', qualified: 'Accepté comme prospect',
        discarded: 'Écarté', spam: 'Déplacé vers les filtrés', new: 'Remis en non ouvert',
        assigned: 'Attribué', note: 'Note',
      },

      actions: {
        contacted: 'Marquer comme répondu', qualify: 'Accepter', discard: 'Écarter',
        delete: 'Supprimer', note: 'Ajouter', cancelAction: 'Annuler', take: 'Je m’en occupe',
      },

      qualify: {
        title: 'Accepter le contact',
        withCrm: 'Une fiche client sera ouverte avec ces données. C’est tout ce qui arrive là-bas : ce que vous ne mettez pas ici n’y sera pas.',
        withoutCrm: 'Le contact est accepté. Sans le module clients installé, aucune fiche n’est ouverte.',
        name: 'Nom du client', taxId: 'Numéro fiscal',
        taxIdHint: 'Si vous le connaissez. Il peut être complété plus tard sur la fiche.',
        done: 'Contact accepté',
      },

      delete: {
        title: 'Supprimer le contact',
        question: 'Il est supprimé pour de bon, pas archivé. C’est ainsi que l’on répond à quelqu’un qui demande à être oublié.',
      },

      captcha: {
        verified: 'Vérifié', unverified: 'N’a pas passé la vérification',
        none: 'Aucune vérification configurée',
      },
      spamReasons: {
        honeypot: 'A rempli un champ caché', too_fast: 'Envoyé trop vite',
        links: 'Trop de liens', captcha: 'N’a pas passé le captcha',
      },

      search: { subtitle: 'Contact depuis le site' },

      notifications: {
        newLead: 'Quelqu’un écrit depuis le site',
        lead: {
          title: 'Nouveau contact depuis le site',
          body: '{name} a écrit depuis {site}{page}.',
        },
      },
    },

    modules: { website: 'Site web' },
    assistant: {
      openers: {
        website: {
          'traffic-week': 'Comment le site a-t-il marché cette semaine ?',
          'new-leads': 'Qui nous a écrit depuis le site ?',
          'top-pages': 'Quelles pages sont les plus vues ?',
        },
      },
    },
    permissions: {
      website: {
        read: 'Voir les visites et les contacts du site',
        write: 'Répondre, accepter et écarter des contacts',
        manage: 'Supprimer des contacts et voir ce qui a été filtré',
      },
    },
    settings: {
      ns: {
        website: {
          title: 'Site web',
          subtitle: 'Qui est prévenu d’un contact, et combien de temps ce qui arrive est conservé',
          notifyLeads: { label: 'Prévenir quand un contact arrive' },
          notifyRoles: {
            label: 'Rôles prévenus',
            help: 'Des rôles, pas des personnes, pour que cela continue à marcher quand quelqu’un part.',
          },
          minimumSeconds: {
            label: 'Secondes minimales avant de croire un formulaire',
            help: 'En dessous, l’envoi est classé comme filtré. Une personne qui écrit met plus de temps que cela.',
          },
          maxLinks: {
            label: 'Liens dans un message avant de le classer comme filtré',
            help: 'À partir de ce nombre, l’envoi est classé comme filtré. Une vraie demande en porte rarement plus d’un.',
          },
          silenceDays: {
            label: 'Jours sans visite avant de déclarer le site muet',
            help: 'Un site qui n’envoie plus de visites, c’est en général le code perdu lors d’une modification du site.',
          },
          leadRetentionDays: {
            label: 'Jours de conservation d’un contact',
            help: 'Zéro les garde jusqu’à ce que quelqu’un en supprime un. Les contacts acceptés sont toujours gardés : la fiche client est ailleurs.',
          },
          spamRetentionDays: {
            label: 'Jours de conservation de ce qui a été filtré',
            help: 'Assez pour vérifier que le filtre n’avale pas de vrais messages, pas assez pour devenir une décharge.',
          },
          trafficRetentionDays: {
            label: 'Jours de conservation des compteurs de visites',
            help: 'Deux ans, pour pouvoir comparer celle-ci avec la précédente. Les compteurs ne nomment personne.',
          },
        },
      },
    },
    audit: {
      actions: {
        website: {
          lead_captured: 'Contact reçu depuis le site',
          lead_filtered: 'Contact filtré automatiquement',
          lead_contacted: 'Contact marqué comme répondu',
          lead_qualified: 'Contact accepté comme prospect',
          lead_discarded: 'Contact écarté',
          lead_spam: 'Contact déplacé vers les filtrés',
          lead_new: 'Contact remis en non ouvert',
          lead_assigned: 'Contact attribué',
          lead_deleted: 'Contact supprimé',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

export const messages = withToolLabels(withAnalytics(baseMessages, 'website'))
